import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { SERVICES_FULL } from "@/data/content";
import { useSEO } from "@/lib/useSEO";

export default function Services() {
  useSEO({
    title: "Services | Tangensis Business Solutions",
    description: "Strategic thinking and practical execution across digital transformation, process optimization, talent acquisition, technology solutions and ERP (Oracle & SAP).",
    path: "/services",
  });
  return (
    <>
      <PageHero
        label="What we do"
        titleLines={["SOLUTIONS DESIGNED", "AROUND YOUR", "BUSINESS."]}
        intro="Strategic thinking and practical execution across the disciplines that move businesses forward."
      />

      <section className="bg-brand-offwhite">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14 md:py-20 space-y-24 md:space-y-32">
          {SERVICES_FULL.map((s, i) => (
            <div key={s.slug} id={s.slug} className="grid grid-cols-1 lg:grid-cols-12 gap-10 scroll-mt-32" data-testid={`service-detail-${s.slug}`}>
              <div className="lg:col-span-4">
                <Reveal>
                  <span className="font-display font-black text-6xl md:text-7xl text-brand-plum/10">{s.num}</span>
                  <h2 className="mt-2 font-heading font-extrabold gradient-text text-3xl md:text-5xl leading-tight tracking-tight">{s.title}</h2>
                  <p className="mt-6 text-brand-plum/70 text-lg leading-relaxed">{s.short}</p>
                  <Link to="/contact" data-testid={`service-cta-${s.slug}`}
                    className="mt-8 inline-flex items-center gap-2 text-brand-ink font-bold border-b-2 border-brand-magenta pb-1 hover:gap-3 transition-all duration-300">
                    START A CONVERSATION <ArrowUpRight className="h-5 w-5" />
                  </Link>
                </Reveal>
              </div>
              <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                <Reveal delay={0.1}>
                  <div className="bg-white rounded-2xl p-8 border border-brand-plum/10 h-full">
                    <h3 className="text-sm uppercase tracking-[0.2em] text-brand-magenta font-semibold">Key capabilities</h3>
                    <ul className="mt-5 space-y-3">
                      {s.capabilities.map((c) => (
                        <li key={c} className="flex items-start gap-3 text-brand-ink/80">
                          <span className="mt-1 h-5 w-5 rounded-full gradient-bg flex items-center justify-center shrink-0"><Check className="h-3 w-3 text-white" /></span>
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
                <Reveal delay={0.2}>
                  <div className="rounded-2xl p-8 gradient-bg-2 h-full text-white">
                    <h3 className="text-sm uppercase tracking-[0.2em] text-brand-ink/80 font-semibold">Benefits</h3>
                    <ul className="mt-5 space-y-3">
                      {s.benefits.map((b) => (
                        <li key={b} className="flex items-start gap-3">
                          <span className="mt-1 h-5 w-5 rounded-full bg-brand-ink/25 flex items-center justify-center shrink-0"><Check className="h-3 w-3 text-brand-ink" /></span>
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTASection heading={["FIND THE RIGHT", "SOLUTION FOR YOU."]} text="Not sure where to start? Let's map it out together." />
    </>
  );
}
